/**
 * NotificationsBell — Wave 3 Batch I bell tray.
 *
 * Mounted in `AppShell` (top-right, next to the project settings link).
 * The list query runs whenever the bell mounts; the unread badge is
 * derived from the All-tab first page's `unreadCount` so we don't need
 * a second roundtrip even when the user is browsing the Unread tab.
 *
 * Wave 3 Batch I (this iteration) restores the legacy tray UX that
 * regressed during the Phase 3b serverless rebuild — see Phase 3a+3b
 * audit R-2. Adds:
 *
 *   1. Date grouping (Today / Yesterday / weekday for last 7 days /
 *      MMM dd, yyyy older).
 *   2. All / Unread tabs (carried over from Wave 1).
 *   3. Per-row 32px `<Avatar>` (image-OR-deterministic-colored-initial).
 *   4. 6-row skeleton loader during initial load (replaces "Loading…").
 *   5. Empty state — center-aligned copy + bell-with-dashed-circle SVG.
 *   6. Infinite scroll via `IntersectionObserver` on a sentinel `<div>`
 *      (replaces the explicit "Load more" button).
 *   7. ~420px wide on desktop with rounded corners + soft shadow,
 *      max-height ~70vh + scroll.
 *   8. Per-row layout: avatar | body (multi-line) | timestamp.
 *   9. Sticky "Mark all as read" footer (hidden when unread = 0).
 *
 * The per-kind verb rendering (`renderNotificationBody` from Wave 3.H)
 * is unchanged — Batch I only changes layout / grouping / avatars /
 * skeleton / empty / infinite-scroll / footer.
 *
 * Realtime push: `useNotificationsRealtime()` (mounted at the page
 * level) invalidates the list cache on `notification.created`, which
 * makes the badge tick up the moment a mention lands.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Notification } from '@/api/types-bridge'
import { AiTag } from '@/components/AiTag'
import { Avatar } from '@/components/Avatar'
import { useMe } from '@/features/me/use-me'
import { AlertTriangle } from 'lucide-react'

import { useMarkAllRead, useMarkRead, useNotifications } from '../use-notifications'
import { groupNotifications, rowTimestamp } from './notification-grouping'
import { renderNotificationBody, renderNotificationText } from './notification-rendering'

interface NotificationsBellProps {
  /**
   * Open the card detail modal for a deliverable. The bell resolves the
   * target project from the notification's own `payload.projectId` (not
   * the current route), so a card mention / assignment / move opens the
   * correct board from anywhere — including the master board. A `null`
   * projectId (a tenant-wide announcement, or a historical row minted
   * before the payload carried one) routes to the master board, which
   * resolves the card against its cross-project aggregate. `commentId`,
   * when present (the comment + comment-mention kinds carry it), scrolls
   * and flashes the specific comment inside the modal's thread.
   */
  onOpenCard: (
    cardId: string,
    projectId: string | null,
    commentId?: string | null,
    parentCommentId?: string | null,
  ) => void
  /**
   * Navigate to a wiki doc by id. The bell calls this for `mention.wiki`
   * notifications whose payload carries a `docId`. AppShell wires it to
   * a TanStack Router `navigate({ to: '/wiki/d/$docId', params })` call
   * — kept as a prop (mirroring `onOpenCard`) so the bell stays
   * router-agnostic + unit-testable. When the prop is omitted (legacy
   * call sites pre-Phase-4), wiki mentions just close the tray on click.
   */
  onOpenWikiDoc?: (docId: string) => void
  /**
   * Navigate to a wiki doc by id AND surface a specific comment thread.
   * Called for `wiki_comment.*` notifications (mention / reply / new /
   * resolved). AppShell wires it through TanStack Router with the
   * `#thread-<id>` URL fragment; `<DocPage>` observes the hash and
   * opens the right rail + scrolls to the matching thread card.
   * Mirrors `onOpenWikiDoc` so the bell stays router-agnostic and tests
   * can stub the navigation surface.
   */
  onOpenWikiThread?: (docId: string, threadId: string) => void
  /**
   * Navigate to a wiki doc AND surface a specific page comment (the
   * Confluence-style flat discussion at the bottom of the doc). Called
   * for `wiki_comment.*` notifications whose payload carries
   * `context === 'page'`. AppShell wires it with a
   * `#page-comment-<id>` URL fragment so `<DocPage>`'s hash observer
   * scrolls + flashes the matching row.
   */
  onOpenWikiPageComment?: (docId: string, threadId: string) => void
  /**
   * Navigate to a chat conversation AND focus a specific message.
   * Called for `mention.chat` notifications whose payload carries
   * `conversationId` + `messageId`. AppShell wires it with a TanStack
   * `focusMessage` search param so the chat surface scrolls + flashes
   * the matching row on mount.
   */
  onOpenChatMention?: (conversationId: string, messageId: string) => void
  /**
   * Navigate to a chat conversation (channel or DM) without focusing a
   * specific message. Called for `channel.member_joined` /
   * `channel.member_left` notifications whose payload carries a
   * `conversationId`. AppShell wires it to a plain
   * `navigate({ to: '/messages/$conversationId' })`.
   */
  onOpenConversation?: (conversationId: string) => void
  /**
   * Navigate to Settings → My Organization (the storage meter). Called for
   * the system `storage.over_limit` notification — the target is static, so
   * no entity id is needed. AppShell wires it to
   * `navigate({ to: '/settings', search: { tab: 'my-organization' } })`.
   */
  onOpenOrgSettings?: () => void
  /**
   * Open an AI agent's runs dialog. Called for `agent.budget_exhausted` /
   * `agent.run_failed`, whose payload carries `agentUserId`. AppShell wires
   * it to the `?agentRuns=<agentUserId>` search param its dialog consumes.
   */
  onOpenAgentRuns?: (agentUserId: string) => void
}

/** System notices have no actor: they render a glyph and a self-contained sentence. */
function isSystemNotification(n: Notification): boolean {
  return (
    n.kind === 'storage.over_limit' ||
    n.kind === 'agent.budget_exhausted' ||
    n.kind === 'agent.run_failed'
  )
}

const MAX_BADGE = 9

function formatBadge(count: number): string {
  if (count <= 0) return ''
  if (count > MAX_BADGE) return `${MAX_BADGE}+`
  return String(count)
}

/**
 * Whether clicking a notification navigates to a source surface. Mirrors
 * the non-navigating branches in `handleRowClick`: `delete-deliverable`
 * (the card no longer exists) and `channel.member_left` (the recipient was
 * removed and can no longer open the conversation) mark-read + close
 * WITHOUT navigating, so their rows render the default cursor; every other
 * kind deep-links and renders a pointer. Keep this in sync with
 * `handleRowClick` — a kind that stops navigating must be added here too.
 */
function isNotificationClickable(n: Notification): boolean {
  return n.kind !== 'delete-deliverable' && n.kind !== 'channel.member_left'
}

/* -------------------------------------------------------------------------- */
/*                                  Icons                                     */
/* -------------------------------------------------------------------------- */

function BellIcon({ title }: { title: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <title>{title}</title>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  )
}

/**
 * Empty-state glyph — a bell-with-dashed-circle. The dashed ring is the
 * "nothing here" cue without leaning on a separate illustration asset.
 */
function EmptyBellIcon({ muted = false, title }: { muted?: boolean; title: string }) {
  const stroke = muted ? 'text-fg-subtle' : 'text-fg-subtle'
  return (
    <svg
      width="80"
      height="80"
      viewBox="0 0 80 80"
      fill="none"
      aria-hidden="true"
      className={stroke}
    >
      <title>{title}</title>
      <circle
        cx="40"
        cy="40"
        r="33"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="4 4"
      />
      <g
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        <path d="M28 35a12 12 0 0 1 24 0c0 14 6 18 6 18H22s6-4 6-18" />
        <path d="M36.6 57.5a3.88 3.88 0 0 0 6.8 0" />
      </g>
    </svg>
  )
}

/* -------------------------------------------------------------------------- */
/*                              Skeleton row                                  */
/* -------------------------------------------------------------------------- */

function SkeletonRow() {
  return (
    <div className="flex items-start gap-3 border-b border-border px-4 py-3 last:border-b-0">
      <div className="h-8 w-8 flex-none animate-pulse rounded-full bg-bg-muted" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="h-3 w-3/4 animate-pulse rounded bg-bg-muted" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-bg-muted" />
      </div>
      <div className="h-3 w-10 flex-none animate-pulse rounded bg-bg-muted" />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                Component                                   */
/* -------------------------------------------------------------------------- */

export function NotificationsBell({
  onOpenCard,
  onOpenWikiDoc,
  onOpenWikiThread,
  onOpenWikiPageComment,
  onOpenChatMention,
  onOpenConversation,
  onOpenOrgSettings,
  onOpenAgentRuns,
}: NotificationsBellProps) {
  const { t, i18n } = useTranslation('notifications')
  const viewerId = useMe().data?.user.id ?? null
  const containerRef = useRef<HTMLDivElement | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  /** Active tab — All vs Unread. Persisted only in component state. */
  const [tab, setTab] = useState<'all' | 'unread'>('all')

  // Keep the badge driven by the All-tab unreadCount even when the user
  // is browsing the Unread tab — otherwise toggling tabs would show
  // stale counts.
  const allList = useNotifications()
  const list = useNotifications({ unreadOnly: tab === 'unread' })
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  const pages = list.data?.pages ?? []
  const items = useMemo(() => pages.flatMap((p) => p.items), [pages])
  const unreadCount = allList.data?.pages[0]?.unreadCount ?? 0

  // `now` recomputes on each render — cheap, and keeps the relative
  // labels honest if the tray sits open across a midnight boundary.
  // The grouping memo pins to `items` so a fresh `Date` doesn't churn
  // the array identity on every render.
  const locale = i18n.language || 'en'
  const groups = useMemo(() => groupNotifications(items, new Date(), locale), [items, locale])

  // Outside-click closes the dropdown. Mirrors `<LabelPicker>` and `<Dropdown>`.
  useEffect(() => {
    if (!open) return
    const handleMouseDown = (event: globalThis.MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])

  // Infinite-scroll sentinel — fetches the next page whenever the empty
  // div at the bottom of the list scrolls into view. We re-attach the
  // observer when `open` flips (tray remounts) and when `hasNextPage`
  // toggles (so the observer drops cleanly once the last page lands).
  // Tab flips swap the cache page, which changes `hasNextPage`, so the
  // effect already re-runs in that case via that dep.
  useEffect(() => {
    if (!open) return
    const node = sentinelRef.current
    if (!node) return
    if (!list.hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && list.hasNextPage && !list.isFetchingNextPage) {
            list.fetchNextPage()
          }
        }
      },
      { root: null, rootMargin: '0px 0px 80px 0px', threshold: 0 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [open, list.hasNextPage, list.isFetchingNextPage, list.fetchNextPage])

  const handleRowClick = (n: Notification) => {
    // Mark read first so the badge updates even if the card lookup fails.
    if (n.readAt === null) {
      markRead.mutate({ id: n.id })
    }

    // Wiki mention deep-link — Phase 4c. The BE notification trigger
    // stamps `docId` on the payload; route to the doc page rather than
    // the (non-existent for this kind) board card. Falls through to the
    // close-only branch when `onOpenWikiDoc` isn't wired (defensive for
    // legacy mounts pre-Phase-4) or when `docId` is missing from a
    // malformed payload.
    if (n.kind === 'mention.wiki') {
      const docId = n.payload.docId
      if (docId && onOpenWikiDoc) {
        onOpenWikiDoc(docId)
      }
      setOpen(false)
      return
    }

    // Wiki comment deep-link. The BE trigger stamps `docId` + `threadId`
    // on every `wiki_comment.*` payload (mention / reply / new /
    // resolved) plus a `context` discriminator (`'thread' | 'page'`)
    // that picks the deep-link target. Rail-style threaded comments
    // route to `#thread-{id}` (the comments rail opens + scrolls);
    // Confluence-style page comments route to `#page-comment-{id}`
    // (the section at the bottom of the doc scrolls + flashes the row).
    // Falls back to `'thread'` for legacy payloads minted before the
    // discriminator shipped, then to the plain doc page when neither
    // deep-link prop is wired (defensive for legacy mounts).
    if (
      n.kind === 'wiki_comment.mention' ||
      n.kind === 'wiki_comment.reply' ||
      n.kind === 'wiki_comment.new' ||
      n.kind === 'wiki_comment.resolved'
    ) {
      const docId = n.payload.docId
      const threadId = n.payload.threadId
      const isPage = n.payload.context === 'page'
      if (docId && threadId && isPage && onOpenWikiPageComment) {
        onOpenWikiPageComment(docId, threadId)
      } else if (docId && threadId && onOpenWikiThread) {
        onOpenWikiThread(docId, threadId)
      } else if (docId && onOpenWikiDoc) {
        onOpenWikiDoc(docId)
      }
      setOpen(false)
      return
    }

    // Chat mention deep-link. The BE trigger stamps `conversationId` +
    // `messageId` on the payload; route to the conversation pane with a
    // `focusMessage` search param so the timeline scrolls + flashes the
    // matching row on mount. Falls through to the close-only branch when
    // either id is missing or the prop isn't wired.
    if (n.kind === 'mention.chat') {
      const conversationId = n.payload.conversationId
      const messageId = n.payload.messageId
      if (conversationId && messageId && onOpenChatMention) {
        onOpenChatMention(conversationId, messageId)
      }
      setOpen(false)
      return
    }

    // Channel-membership deep-link. `member_joined` carries a
    // `conversationId` and routes to the channel. `member_left` does NOT
    // navigate — the recipient was just removed and can no longer open the
    // conversation (the fetch would 403), so it marks read + closes like
    // `delete-deliverable`.
    if (n.kind === 'channel.member_joined') {
      const conversationId = n.payload.conversationId
      if (conversationId && onOpenConversation) {
        onOpenConversation(conversationId)
      }
      setOpen(false)
      return
    }
    if (n.kind === 'channel.member_left') {
      setOpen(false)
      return
    }

    // Storage over-limit — system notice with no entity id; deep-link to
    // the storage meter in Settings → My Organization.
    if (n.kind === 'storage.over_limit') {
      onOpenOrgSettings?.()
      setOpen(false)
      return
    }

    // Agent budget / failed-run notices — open that agent's Runs. A payload
    // without the agent id (malformed) just closes the tray.
    if (n.kind === 'agent.budget_exhausted' || n.kind === 'agent.run_failed') {
      const agentUserId = n.payload.agentUserId
      if (agentUserId && onOpenAgentRuns) {
        onOpenAgentRuns(agentUserId)
      }
      setOpen(false)
      return
    }

    const cardId = n.payload.cardId
    if (!cardId) {
      setOpen(false)
      return
    }

    // `delete-deliverable` has no openable target — the card is gone, so a
    // deep-link would land on an empty-board flash + rollback. Match the
    // legacy behaviour: mark read (done above) and close the tray without
    // navigating.
    if (n.kind === 'delete-deliverable') {
      setOpen(false)
      return
    }

    // Resolve the destination from the notification's own payload so the
    // click works from any route. `projectId` is stamped on every
    // card-based notification (null only for tenant-wide announcements);
    // AppShell routes a null projectId to the master board. `commentId`
    // (set on comment + comment-mention kinds) deep-links to the specific
    // comment inside the card modal's thread.
    onOpenCard(
      cardId,
      n.payload.projectId ?? null,
      n.payload.commentId ?? null,
      n.payload.parentCommentId ?? null,
    )
    setOpen(false)
  }

  const handleMarkAll = () => {
    markAllRead.mutate()
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault()
          setOpen(false)
        }
      }}
    >
      <button
        type="button"
        aria-label={t('bell.buttonAria')}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t('bell.buttonTitle')}
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg focus:outline-none focus:ring-2 focus:ring-fg"
      >
        <BellIcon title={t('bell.iconTitle')} />
        {unreadCount > 0 && (
          <span
            aria-label={t('bell.unreadBadgeAria', { count: unreadCount })}
            className="absolute -end-0.5 -top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-bg-elevated"
          >
            {formatBadge(unreadCount)}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t('bell.menuAriaLabel')}
          className="absolute end-0 z-40 mt-2 flex w-[420px] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-xl border border-border bg-bg-elevated shadow-2xl shadow-slate-900/10 ring-1 ring-black/5 focus:outline-none"
          style={{ maxHeight: '70vh' }}
        >
          {/* Header — title + close affordance via Escape. */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="text-sm font-semibold text-fg">{t('bell.title')}</span>
            <span className="text-xs text-fg-subtle">
              {unreadCount > 0
                ? t('bell.unreadCount', { count: unreadCount })
                : t('bell.allCaughtUp')}
            </span>
          </div>

          {/* Tab strip — All / Unread. Restyled for the wider tray. */}
          <div
            role="tablist"
            aria-label={t('bell.tabsAriaLabel')}
            className="flex gap-1 border-b border-border bg-bg-muted px-2 py-1.5"
          >
            {(
              [
                { value: 'all', label: t('bell.tabAll') },
                { value: 'unread', label: t('bell.tabUnread') },
              ] as const
            ).map((tab1) => {
              const active = tab === tab1.value
              return (
                <button
                  key={tab1.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(tab1.value)}
                  className={[
                    'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-fg-subtle',
                    active
                      ? 'bg-bg-elevated text-fg shadow-sm ring-1 ring-border-strong'
                      : 'text-fg-muted hover:text-fg',
                  ].join(' ')}
                >
                  {tab1.label}
                  {tab1.value === 'unread' && unreadCount > 0 && (
                    <span className="ms-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          {/* Body — skeleton on first load, empty-state when no items, else
              date-grouped list. The body is the only scrollable region; the
              header / tabs / footer stay pinned. */}
          <div className="flex-1 overflow-y-auto">
            {list.isLoading ? (
              <div>
                {Array.from({ length: 6 }, (_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no stable id
                  <SkeletonRow key={i} />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                <EmptyBellIcon muted={tab === 'unread'} title={t('bell.emptyIconTitle')} />
                <p className="mt-4 text-sm font-medium text-fg">
                  {tab === 'unread' ? t('bell.emptyTitleUnread') : t('bell.emptyTitleAll')}
                </p>
                <p className="mt-1 text-xs text-fg-muted">
                  {tab === 'unread' ? t('bell.emptySubtitleUnread') : t('bell.emptySubtitleAll')}
                </p>
              </div>
            ) : (
              <div>
                {groups.map((group) => (
                  <section key={group.label}>
                    <header className="sticky top-0 z-10 border-b border-border bg-bg-muted/95 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-fg-muted backdrop-blur-sm">
                      {group.label}
                    </header>
                    {group.items.map((n) => {
                      const unread = n.readAt === null
                      const clickable = isNotificationClickable(n)
                      const actorName = n.payload.actorName ?? t('rendering.fallbackActor')
                      const avatarUrl =
                        (n.payload as { actorAvatarUrl?: string | null }).actorAvatarUrl ?? null
                      // Seed the avatar color by the stable
                      // actor id (not name), so the same person reads as
                      // the same color across notification rows + every
                      // other surface that renders them.
                      const actorId = (n.payload as { actorId?: string | null }).actorId ?? null
                      // System notices (no actor) render a glyph + a
                      // self-contained sentence instead of an actor avatar +
                      // name prefix.
                      const isSystem = isSystemNotification(n)
                      return (
                        <button
                          key={n.id}
                          type="button"
                          aria-label={renderNotificationText(n, viewerId)}
                          onClick={() => handleRowClick(n)}
                          className={`flex w-full items-start gap-3 border-b border-border px-4 py-3 text-start transition-colors last:border-b-0 hover:bg-bg-muted focus:bg-bg-muted focus:outline-none ${
                            clickable ? 'cursor-pointer' : 'cursor-default'
                          } ${unread ? 'bg-sky-50/60 dark:bg-sky-900/20' : ''}`}
                        >
                          <div className="relative flex-none">
                            {isSystem ? (
                              <div
                                aria-hidden="true"
                                className="flex h-8 w-8 items-center justify-center rounded-full bg-danger/10 text-danger"
                              >
                                <AlertTriangle className="h-4 w-4" />
                              </div>
                            ) : (
                              <Avatar
                                src={avatarUrl}
                                name={actorName}
                                colorSeed={actorId ?? actorName}
                              />
                            )}
                            {unread && (
                              <span
                                aria-hidden="true"
                                className="absolute -end-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-sky-500 ring-2 ring-bg-elevated"
                              />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm leading-snug text-fg">
                              {isSystem ? (
                                renderNotificationBody(n, viewerId)
                              ) : (
                                <>
                                  <span className="font-semibold text-fg">{actorName}</span>{' '}
                                  <AiTag
                                    kind={n.payload.actorKind}
                                    userId={n.payload.actorId}
                                    className="align-middle"
                                  />{' '}
                                  {renderNotificationBody(n, viewerId)}
                                </>
                              )}
                            </p>
                            {n.payload.snippet && (
                              <p className="mt-1 line-clamp-2 text-xs text-fg-muted">
                                {n.payload.snippet}
                              </p>
                            )}
                          </div>
                          <span className="ms-1 flex-none whitespace-nowrap text-xs text-fg-subtle">
                            {rowTimestamp(n.createdAt, new Date(), locale)}
                          </span>
                        </button>
                      )
                    })}
                  </section>
                ))}

                {/* Infinite-scroll sentinel + spinner. The IntersectionObserver
                    above triggers `fetchNextPage` when this scrolls into view. */}
                {list.hasNextPage && (
                  <div
                    ref={sentinelRef}
                    className="flex items-center justify-center px-4 py-4 text-xs text-fg-subtle"
                  >
                    {list.isFetchingNextPage ? t('bell.loading') : ''}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sticky footer — Mark all as read. Hidden when there are no
              unread items so the tray stays compact when the user is
              already at zero. */}
          {unreadCount > 0 && (
            <div className="border-t border-border bg-bg-elevated px-4 py-2.5">
              <button
                type="button"
                onClick={handleMarkAll}
                disabled={markAllRead.isPending}
                className="w-full rounded-md py-1.5 text-center text-xs font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-60 dark:text-sky-300 dark:hover:bg-sky-900/40"
              >
                {markAllRead.isPending ? t('bell.markingAll') : t('bell.markAll')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
